'use client';

import { useState } from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { useImageSrc } from '@/components/media/MediaOverrideContext';
import { imageFocalPoints } from '@/data/image-focal-points';
import {
  Sparkles,
  Mountain,
  TreePine,
  Footprints,
  Zap,
  Heart,
  Car,
  Flame,
  Fish,
  Home,
  Sunset,
  Waves,
  Leaf,
  type LucideIcon,
} from 'lucide-react';

type ImageCategory =
  | 'winter-hero'
  | 'summer-hero'
  | 'winter-activity'
  | 'summer-activity'
  | 'resort'
  | 'cabin'
  | 'landscape';

const gradients: Record<ImageCategory, string> = {
  'winter-hero': 'from-[#0f172a] via-[#1e1b4b] to-[#064e3b]',
  'summer-hero': 'from-[#fbbf24] via-[#fde68a] to-[#7dd3fc]',
  'winter-activity': 'from-[#334155] via-[#475569] to-[#1e3a5f]',
  'summer-activity': 'from-[#166534] via-[#4ade80] to-[#fbbf24]',
  resort: 'from-[#1e1b4b] via-[#312e81] to-[#4c1d95]',
  cabin: 'from-[#92400e] via-[#b45309] to-[#78716c]',
  landscape: 'from-[#475569] via-[#64748b] to-[#2dd4bf]',
};

const iconPatterns: Array<[RegExp, LucideIcon]> = [
  [/northern.?lights|aurora/i, Sparkles],
  [/cross.?country|nordic/i, TreePine],
  [/skiing|ski|snowboard/i, Mountain],
  [/husky|dog.?sled/i, Footprints],
  [/snowmobile/i, Zap],
  [/reindeer/i, Heart],
  [/ice.?kart|karting/i, Car],
  [/snowshoe/i, Footprints],
  [/hiking|trek|fell/i, Mountain],
  [/sauna/i, Flame],
  [/fish/i, Fish],
  [/cabin|living|bedroom|kitchen|fireplace/i, Home],
  [/sunset|sunrise|midnight.?sun/i, Sunset],
  [/lake|kayak|canoe|paddle/i, Waves],
  [/berry|picking|forag/i, Leaf],
];

function getIconForContent(src: string, alt: string): LucideIcon {
  const combined = `${src} ${alt}`;
  for (const [pattern, icon] of iconPatterns) {
    if (pattern.test(combined)) {
      return icon;
    }
  }
  return Mountain;
}

interface SmartImageProps {
  src: string;
  alt: string;
  category?: ImageCategory;
  width: number;
  height: number;
  fill?: boolean;
  className?: string;
  priority?: boolean;
  sizes?: string;
}

export default function SmartImage({
  src,
  alt,
  category,
  width,
  height,
  fill,
  className,
  priority,
  sizes,
}: SmartImageProps) {
  const resolvedSrc = useImageSrc(src);
  const objectPosition = className?.includes('object-contain') ? undefined : imageFocalPoints[resolvedSrc];
  const [hasError, setHasError] = useState(false);

  const resolvedCategory = category ?? 'cabin';

  if (hasError) {
    const gradient = gradients[resolvedCategory];
    const isDark = [
      'winter-hero',
      'winter-activity',
      'resort',
      'cabin',
      'landscape',
    ].includes(resolvedCategory);

    const Icon = getIconForContent(src, alt);

    return (
      <div
        className={cn(
          'relative overflow-hidden bg-gradient-to-br',
          gradient,
          fill ? 'absolute inset-0' : '',
          className
        )}
        style={fill ? undefined : { width, height }}
      >
        <div
          className={cn(
            'absolute inset-0 -translate-x-full animate-[shimmer_3s_ease-in-out_infinite]',
            isDark ? 'bg-gradient-to-r from-transparent via-white/5 to-transparent'
              : 'bg-gradient-to-r from-transparent via-white/20 to-transparent'
          )}
        />
        <svg
          className="absolute inset-x-0 bottom-0 h-1/3 w-full"
          viewBox="0 0 400 200"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <polygon
            points="0,200 60,80 130,140 200,50 280,120 350,70 400,130 400,200"
            className={isDark ? 'fill-white/5' : 'fill-black/5'}
          />
          <polygon
            points="0,200 80,120 160,170 240,90 320,150 400,100 400,200"
            className={isDark ? 'fill-white/[0.07]' : 'fill-black/[0.07]'}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
          <Icon
            className={cn(
              'h-12 w-12 animate-pulse',
              isDark ? 'text-white/30' : 'text-black/20'
            )}
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <span
            className={cn(
              'text-xs font-medium tracking-widest uppercase text-center max-w-[80%] leading-relaxed',
              isDark ? 'text-white/50' : 'text-black/35'
            )}
          >
            {alt}
          </span>
        </div>
      </div>
    );
  }

  if (fill) {
    return (
      <Image
        src={resolvedSrc}
        alt={alt}
        fill
        className={cn('object-cover', className)}
        style={{ objectPosition }}
        onError={() => setHasError(true)}
        priority={priority}
        sizes={sizes ?? "100vw"}
      />
    );
  }

  return (
    <Image
      src={resolvedSrc}
      alt={alt}
      width={width}
      height={height}
      className={cn('object-cover', className)}
      style={{ objectPosition }}
      onError={() => setHasError(true)}
      priority={priority}
    />
  );
}
